import React, { useState, useEffect } from "react";
import "../../Styles/ManageUsers.css";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

function ManageUsers() {
  const [searchQuery, setSearchQuery] = useState("");
  const [admins, setAdmins] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const recordsPerPage = 10;
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }
      setLoading(true);
      const response = await axios.get(
        "https://api.uniums.com/api/User/manage-admins",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        const admins = response.data.data;

        const plansResponse = await axios.get(
          "https://api.uniums.com/api/User/get-subscription-plans",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const plans = plansResponse.data.success ? plansResponse.data.data : [];

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison

        const enrichedAdmins = admins.map((admin) => {
          const plan = plans.find((p) => p.adminId === admin._id) || {};
          const fromDate = plan.fromDate ? new Date(plan.fromDate) : null;
          const toDate = plan.toDate ? new Date(plan.toDate) : null;

          // Calculate total days
          const totalDays = plan.days || "N/A";

          // Calculate remaining days
          let remainingDays = "N/A";
          if (toDate) {
            const diffTime = Math.max(0, toDate - today);
            remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Auto-block user if days remaining is 0
            if (remainingDays === 0 && admin.active) {
              // We'll handle the auto-blocking in useEffect
            }
          }

          return {
            ...admin,
            subscriptionFromDate: fromDate
              ? fromDate.toISOString().split("T")[0]
              : "",
            subscriptionToDate: toDate
              ? toDate.toISOString().split("T")[0]
              : "",
            subscriptionDays: totalDays,
            remainingDays: remainingDays,
            approvedUsers: plan.approvedUsers || admin.approvedUsers || 0,
          };
        });

        setAdmins(enrichedAdmins);

        // Auto-block expired subscriptions
        enrichedAdmins.forEach((admin) => {
          if (admin.remainingDays === 0 && admin.active) {
            toggleActiveStatus(admin._id, true, true); // true, true = current status is active, silent mode
          }
        });
      } else {
        console.error("Failed to fetch admins:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false); // Hide loader after API call completes
    }
  };

  useEffect(() => {
    fetchAdmins();

    // Set up a daily check for expired subscriptions
    const dailyCheck = setInterval(() => {
      fetchAdmins(); // This will also handle auto-blocking
    }, 86400000); // 24 hours in milliseconds

    return () => clearInterval(dailyCheck);
  }, [searchQuery, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Modified to support silent mode for auto-blocking
  const toggleActiveStatus = async (id, currentStatus, silent = false) => {
    try {
      if (!silent) setLoading(true); // Show loader for user-initiated actions only

      const newStatus = !currentStatus;
      const response = await axios.put(
        `https://api.uniums.com/api/User/toggle-active/${id}`,
        {
          active: newStatus,
        }
      );

      if (response.data.success) {
        setAdmins(
          admins.map((admin) =>
            admin._id === id ? { ...admin, active: newStatus } : admin
          )
        );

        if (!silent) {
          toast.success(
            `Admin ${newStatus ? "activated" : "deactivated"} successfully!`
          );
        } else if (!newStatus) {
          toast.info(
            `Admin account automatically deactivated due to expired subscription.`
          );
        }
      } else {
        if (!silent) {
          toast.error("Failed to update active status");
        }
      }
    } catch (error) {
      console.error("Error updating active status:", error);
      if (!silent) {
        toast.error("An error occurred while updating status");
      }
    } finally {
      if (!silent) setLoading(false); // Hide loader after API call completes
    }
  };

  const handleFromDateChange = (e, adminId) => {
    const date = e.target.value;
    setAdmins((prevAdmins) => {
      return prevAdmins.map((admin) =>
        admin._id === adminId ? { ...admin, subscriptionFromDate: date } : admin
      );
    });
    calculateDays(adminId);
  };

  const handleToDateChange = (e, adminId) => {
    const date = e.target.value;
    setAdmins((prevAdmins) => {
      return prevAdmins.map((admin) =>
        admin._id === adminId ? { ...admin, subscriptionToDate: date } : admin
      );
    });
    calculateDays(adminId);
  };

  const calculateDays = (adminId) => {
    setAdmins((prevAdmins) => {
      const admin = prevAdmins.find((admin) => admin._id === adminId);
      if (admin && admin.subscriptionFromDate && admin.subscriptionToDate) {
        const fromDate = new Date(admin.subscriptionFromDate);
        const toDate = new Date(admin.subscriptionToDate);
        const diffTime = Math.abs(toDate - fromDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Calculate remaining days too
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const remainingTime = Math.max(0, toDate - today);
        const remainingDays = Math.ceil(remainingTime / (1000 * 60 * 60 * 24));

        return prevAdmins.map((a) =>
          a._id === adminId
            ? {
                ...a,
                subscriptionDays: diffDays > 0 ? diffDays : "N/A",
                remainingDays: diffDays > 0 ? remainingDays : "N/A",
                isAddPlanEnabled: diffDays > 0,
              }
            : a
        );
      }
      return prevAdmins;
    });
  };

  const handleApprovedUsersChange = (e, adminId) => {
    const value = e.target.value;
    setAdmins((prevAdmins) =>
      prevAdmins.map((admin) =>
        admin._id === adminId ? { ...admin, approvedUsers: value } : admin
      )
    );
  };

  const handleAddPlan = async (adminId) => {
    const admin = admins.find((admin) => admin._id === adminId);
    if (
      admin &&
      admin.subscriptionFromDate &&
      admin.subscriptionToDate &&
      admin.subscriptionDays !== "N/A"
    ) {
      try {
        const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }
        setLoading(true); // Show loader before API call
        const response = await axios.post(
          "https://api.uniums.com/api/User/add-subscription-plan",
          {
            adminId,
            fromDate: admin.subscriptionFromDate,
            toDate: admin.subscriptionToDate,
            days: admin.subscriptionDays,
            approvedUsers: admin.approvedUsers || 0,
          },{
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          toast.success(response.data.message);
          fetchAdmins();
        } else {
          toast.error("Failed to save subscription plan");
        }
      } catch (error) {
        console.error("Error saving subscription plan:", error);
        toast.error("An error occurred while saving subscription plan");
      } finally {
        setLoading(false); // Hide loader after API call completes
      }
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }
      setLoading(true); // Show loader before API call
      const response = await axios.get(
        "https://api.uniums.com/api/User/get-subscription-plans",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setSubscriptionPlans(response.data.data);
      } else {
        console.error(
          "Failed to fetch subscription plans:",
          response.data.message
        );
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    } finally {
      setLoading(false); // Hide loader after API call completes
    }
  };

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  const handleEditPlan = async (planId) => {
    const plan = subscriptionPlans.find((p) => p._id === planId);
    if (plan) {
      const updatedPlan = {
        fromDate: plan.fromDate,
        toDate: plan.toDate,
        days: plan.days,
      };

      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found");
          return;
        }
        setLoading(true); // Show loader before API call
        const response = await axios.put(
          `https://api.uniums.com/api/User/update-subscription-plan/${planId}`,
          updatedPlan,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.success) {
          toast.success("Subscription plan updated successfully");
          fetchSubscriptionPlans();
        } else {
          toast.error("Failed to update subscription plan");
        }
      } catch (error) {
        console.error("Error updating subscription plan:", error);
        toast.error("An error occurred while updating subscription plan");
      } finally {
        setLoading(false); // Hide loader after API call completes
      }
    }
  };

  return (
    <div className="manage-users-container">
      {loading && <Loader />} {/* Show loader when loading is true */}
      <h1>Manage Admins</h1>
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by Admin Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>SL No</th>
              <th>Organization Name</th>
              <th>Admin Name</th>
              <th>Mobile Number</th>
              <th>Address</th>
              <th>Users Limit</th>
              <th>From Date</th>
              <th>To Date</th>
              <th>Total Days</th>
              <th>Remaining Days</th>
              <th>Subscription Plan</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin, index) => {
              const fromDate = admin.subscriptionFromDate || "";
              const toDate = admin.subscriptionToDate || "";
              const diffDays = admin.subscriptionDays || "N/A";
              const remainingDays = admin.remainingDays || "N/A";

              // Status color based on remaining days
              let remainingDaysStyle = {};
              if (remainingDays !== "N/A") {
                if (remainingDays <= 0) {
                  remainingDaysStyle = { color: "red", fontWeight: "bold" };
                } else if (remainingDays <= 5) {
                  remainingDaysStyle = { color: "orange", fontWeight: "bold" };
                } else {
                  remainingDaysStyle = { color: "green" };
                }
              }

              return (
                <tr key={admin._id}>
                  <td>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                  <td>{admin.organizationName}</td>
                  <td>{admin.name}</td>
                  <td>{admin.organizationContact}</td>
                  <td>{admin.organizationAddress}</td>
                  <td>
                    <input
                      type="number"
                      value={admin.approvedUsers || ""}
                      onChange={(e) => handleApprovedUsersChange(e, admin._id)}
                      placeholder="Approved Users"
                      className="approve_users"
                    />
                  </td>
                  <td className="input_type_date">
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => handleFromDateChange(e, admin._id)}
                      min={new Date().toISOString().split("T")[0]}
                      max={toDate}
                    />
                  </td>
                  <td className="input_type_date">
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => handleToDateChange(e, admin._id)}
                      min={fromDate}
                    />
                  </td>
                  <td>{diffDays}</td>
                  <td style={remainingDaysStyle}>{remainingDays}</td>
                  <td className="input_date_btn">
                    <button
                      onClick={() => handleAddPlan(admin._id)}
                      disabled={admin.subscriptionDays === "N/A" || loading}
                    >
                      {" "}
                      Add Plan{" "}
                    </button>
                  </td>

                  <td className="action_button_container">
                    <button
                      className="status-button activate"
                      onClick={() =>
                        toggleActiveStatus(admin._id, admin.active)
                      }
                      disabled={
                        admin.active ||
                        (remainingDays === 0 && remainingDays !== "N/A") ||
                        loading
                      }
                    >
                      Activate
                    </button>
                    <button
                      className="status-button deactivate"
                      onClick={() =>
                        toggleActiveStatus(admin._id, admin.active)
                      }
                      disabled={!admin.active || loading}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <ToastContainer />
      </div>
      {/* Pagination */}
      <div className="pagination">
        {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
          <button
            key={page}
            className={`pagination-button ${
              page === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(page)}
            disabled={loading}
          >
            {page}
          </button>
        ))}
      </div>
    </div>
  );
}

export default ManageUsers;
